import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Check, Zap } from "lucide-react";

const TIERS = [
    {
        name: "Standard",
        price: "$150",
        cadence: "per promotion",
        description:
            "Newswire to 75 newspapers, magazines & online media (metro area). One main image plus full social posts and chronological blog placement.",
        features: [
            "1 main image",
            "Newswire to 75 metro outlets",
            "Social media posts (IG + cross-channel)",
            "Permanent blog post on BENNE",
            "Reviewed within 48 hours",
        ],
        cta: { label: "Submit a Standard release", to: "/submit-press-release" },
        highlight: false,
    },
    {
        name: "Enhanced",
        price: "$225",
        cadence: "per promotion",
        description:
            "Newswire to 150 newspapers, magazines & online media (statewide). Two images plus logo, expanded socials and priority editorial review.",
        features: [
            "2 images + logo",
            "Newswire to 150 statewide outlets",
            "Expanded social media coverage",
            "Permanent blog post on BENNE",
            "Priority editorial review",
        ],
        cta: { label: "Submit an Enhanced release", to: "/submit-press-release" },
        highlight: true,
    },
    {
        name: "Premium",
        price: "$275",
        cadence: "per promotion",
        description:
            "Newswire to 360 newspapers, magazines & online media (regional + national). Two images, one video and a logo — the full promotional treatment.",
        features: [
            "2 images + 1 video + logo",
            "Newswire to 360 regional/national outlets",
            "Featured social media campaign",
            "Permanent blog post on BENNE",
            "Priority editorial review",
        ],
        cta: { label: "Submit a Premium release", to: "/submit-press-release" },
        highlight: false,
    },
];

export default function BillingPage() {
    return (
        <div data-testid="billing-page">
            <PageHero
                eyebrow="Pricing"
                title="Billing"
                lead="Three transparent tiers, plus an optional expedited slot. Pay securely with Stripe at the end of the submission form."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Submit a Press Release", to: "/submit-press-release" },
                    { label: "Billing" },
                ]}
            />
            <div className="px-4 md:px-8 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl">
                {TIERS.map((t) => (
                    <article
                        key={t.name}
                        className={`relative border ${
                            t.highlight
                                ? "border-[#E63B26] bg-[#1B1B1B] text-white"
                                : "border-black/10 bg-white text-[#1B1B1B]"
                        } p-7 flex flex-col`}
                        data-testid={`billing-tier-${t.name.toLowerCase()}`}
                    >
                        {t.highlight && (
                            <span className="absolute -top-3 left-7 bg-[#F5C53D] text-[#1B1B1B] font-poppins text-[0.65rem] tracking-[0.2em] uppercase px-3 py-1">
                                Most chosen
                            </span>
                        )}
                        <h3 className="font-poppins font-bold text-2xl">{t.name}</h3>
                        <p className="mt-3 font-poppins font-black text-4xl">
                            {t.price}
                            <span
                                className={`ml-2 font-sofia font-normal text-sm ${
                                    t.highlight ? "text-white/60" : "text-[#1B1B1B]/60"
                                }`}
                            >
                                / {t.cadence}
                            </span>
                        </p>
                        <p
                            className={`mt-3 font-sofia ${
                                t.highlight ? "text-white/85" : "text-[#1B1B1B]/80"
                            }`}
                        >
                            {t.description}
                        </p>
                        <ul className="mt-5 space-y-2 font-sofia">
                            {t.features.map((f) => (
                                <li key={f} className="flex items-start gap-2">
                                    <Check
                                        className={`h-4 w-4 mt-1 shrink-0 ${
                                            t.highlight ? "text-[#F5C53D]" : "text-[#E63B26]"
                                        }`}
                                    />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        <Link
                            to={t.cta.to}
                            className={`mt-7 inline-block text-center font-poppins tracking-[0.18em] uppercase text-xs py-3 transition ${
                                t.highlight
                                    ? "bg-[#F5C53D] text-[#1B1B1B] hover:bg-[#E63B26] hover:text-white"
                                    : "bg-[#E63B26] text-white hover:bg-[#1B1B1B]"
                            }`}
                            data-testid={`billing-cta-${t.name.toLowerCase()}`}
                        >
                            {t.cta.label} →
                        </Link>
                    </article>
                ))}
            </div>

            <div
                className="px-4 md:px-8 lg:px-12 pb-20 max-w-6xl"
                data-testid="billing-expedite"
            >
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 bg-[#F5C53D] text-[#1B1B1B] p-6">
                    <div className="bg-[#1B1B1B] text-[#F5C53D] p-3">
                        <Zap className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                        <h4 className="font-poppins font-bold text-xl">
                            Expedite this release · +$75
                        </h4>
                        <p className="font-sofia mt-1">
                            Push your post live in 1–5 business hours (Mon–Fri).
                            Stack on top of any tier. Verified obituaries are
                            expedited for free.
                        </p>
                    </div>
                    <Link
                        to="/submit-press-release"
                        className="font-poppins tracking-[0.18em] uppercase text-xs bg-[#1B1B1B] text-[#F5C53D] hover:bg-white hover:text-[#1B1B1B] py-3 px-5 transition"
                        data-testid="expedite-cta"
                    >
                        Add expedite at checkout →
                    </Link>
                </div>
            </div>
        </div>
    );
}
